import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, RefreshControl } from 'react-native';
import { Text, Card, ActivityIndicator, Menu, Button, Divider, Chip } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import api from '../../services/api';

const rolLabel = (r) => ({ admin: 'Administrador', vendedor: 'Vendedor', bodega: 'Bodeguero', repartidor: 'Repartidor', supervisor: 'Supervisor', gerente: 'Gerente' }[r] || r);

const UsuariosSucursalScreen = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [menuUserId, setMenuUserId] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [us, sucs] = await Promise.all([api.getUsuariosGrupo(), api.getSucursales()]);
      setUsuarios(Array.isArray(us) ? us : []);
      setSucursales(Array.isArray(sucs) ? sucs : []);
    } catch (e) {
      Alert.alert('Error', e.message || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { cargar(); }, [cargar]));

  const mover = async (usuario, destino) => {
    setMenuUserId(null);
    if (destino.id === usuario.tenant_id) return;
    try {
      await api.moverUsuario(usuario.id, destino.id);
      await cargar();
      Alert.alert('Listo', `${usuario.nombre} ahora pertenece a "${destino.nombre}".`);
    } catch (e) {
      Alert.alert('Error', e.message || 'No se pudo mover el usuario');
    }
  };

  // Agrupa usuarios por sucursal
  const grupos = sucursales.map((s) => ({
    sucursal: s,
    usuarios: usuarios.filter((u) => u.tenant_id === s.id),
  })).filter((g) => g.usuarios.length > 0);

  if (loading) {
    return <View style={styles.center}><ActivityIndicator size="large" color="#007bff" /></View>;
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scroll}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); cargar(); }} />}
    >
      <Text style={styles.hint}>Mueve un usuario a otra sucursal para que opere ahí. Cada usuario pertenece a una sola sucursal.</Text>

      {grupos.map((g) => (
        <View key={g.sucursal.id} style={styles.grupo}>
          <View style={styles.grupoHeader}>
            <Ionicons name="business" size={16} color="#0d47a1" />
            <Text style={styles.grupoNombre}>{g.sucursal.nombre}</Text>
            {g.sucursal.es_matriz ? <Text style={styles.matrizTag}>Matriz</Text> : null}
            <Text style={styles.grupoCount}>{g.usuarios.length}</Text>
          </View>

          {g.usuarios.map((u) => (
            <Card key={u.id} style={styles.card}>
              <Card.Content style={styles.cardContent}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.nombre}>{u.nombre}</Text>
                  <Text style={styles.email}>{u.email}</Text>
                  <Chip compact style={styles.rolChip} textStyle={styles.rolChipText}>{rolLabel(u.rol)}</Chip>
                </View>
                <Menu
                  visible={menuUserId === u.id}
                  onDismiss={() => setMenuUserId(null)}
                  anchor={
                    <Button mode="outlined" compact icon="swap-horizontal" onPress={() => setMenuUserId(u.id)}>
                      Mover
                    </Button>
                  }
                >
                  <Text style={styles.menuTitle}>Mover a…</Text>
                  <Divider />
                  {sucursales.filter((s) => s.id !== u.tenant_id).map((s) => (
                    <Menu.Item key={s.id} title={s.nombre + (s.es_matriz ? ' (Matriz)' : '')} onPress={() => mover(u, s)} />
                  ))}
                  {sucursales.length <= 1 && <Menu.Item title="No hay otras sucursales" disabled />}
                </Menu>
              </Card.Content>
            </Card>
          ))}
        </View>
      ))}

      {grupos.length === 0 && <Text style={styles.empty}>No hay usuarios.</Text>}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 60 },
  scroll: { padding: 16, paddingBottom: 40 },
  hint: { fontSize: 13, color: '#666', marginBottom: 14, lineHeight: 18 },
  grupo: { marginBottom: 18 },
  grupoHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  grupoNombre: { fontSize: 15, fontWeight: 'bold', color: '#0d47a1', marginLeft: 6 },
  matrizTag: { fontSize: 10, color: '#007bff', backgroundColor: '#e3f2fd', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, overflow: 'hidden', marginLeft: 8 },
  grupoCount: { marginLeft: 'auto', fontSize: 12, color: '#888', fontWeight: 'bold' },
  card: { backgroundColor: '#fff', marginBottom: 8 },
  cardContent: { flexDirection: 'row', alignItems: 'center' },
  nombre: { fontSize: 15, fontWeight: '600', color: '#222' },
  email: { fontSize: 12, color: '#777', marginTop: 1 },
  rolChip: { alignSelf: 'flex-start', marginTop: 6, backgroundColor: '#eef2f7', height: 24 },
  rolChipText: { fontSize: 11, color: '#555' },
  menuTitle: { fontSize: 12, color: '#888', paddingHorizontal: 12, paddingVertical: 6 },
  empty: { textAlign: 'center', color: '#888', marginTop: 30 },
});

export default UsuariosSucursalScreen;
